import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { erApiFeil, erNettfeil } from '../api/feil.ts';
import { useKilder, useVelgForslag, type MetricSource, type MetricSourceRef, type SourceQuestion } from '../api/useHypoteseKilder.ts';
import { useHentHypotesePaaNytt, useHypotese, useHypoteseConfig, useOppfriskConfig, useSendInn, type Hypothesis } from '../api/useHypotheses.ts';
import { useMe, useOppfriskMeg } from '../api/useMe.ts';
import { useKart } from '../skall/kart.tsx';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import type { Tast } from '../innboks/Tastaturkart.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { Brikker, Flate, Fot, Lagret, Tekstknapp, Varsel } from './deler.tsx';
import { fyltInn } from './kilder.ts';
import { frekvensTekst, LeddFelt, paastandTekst, svarFor } from './LeddFelt.tsx';
import { fornavn, navnAv, stor } from './lesning.ts';
import { LignerPaa } from './LignerPaa.tsx';
import { NavnSide } from './NavnSide.tsx';
import { laasgrunn, leddFylt, porter, TITTEL_MIN, type Mangel } from './porter.ts';
import { delsetning, kortDato, kortDatoAv } from './setning.ts';
import { VetIkkeTallet, type Vei } from './VetIkkeTallet.tsx';
import { forLange, LEDD, LES_OVER, leddFor, type Felt, type LeddNoekkel, type Skjema } from './skjema.ts';
import { useUtkastLagring, type Konflikt } from './useUtkastLagring.ts';

/**
 * Innsendingen (H2.2): skjemaet (W2, W3, W3b; M2), «Les over» (W4, W5; M3a) og veien til
 * kvitteringen. Én komponent for `/h/ny` og `/h/utkast/{id}/{ledd}`, så teksten i minnet
 * overlever at adressen får utkastets id etter første lagring.
 *
 * Over 900: leddene som liste til venstre, ETT åpent om gangen, setningen til høyre (sticky)
 * med leietakerens hjelpetekst. Under 900: ett spørsmål per skjerm, setningen øverst, «Neste»
 * i tommelsonen (DD-51, DD-57). Adressen bærer leddet, så nettleserens tilbake-knapp går ett
 * ledd tilbake.
 */

const MINE = { navn: 'Mine hypoteser', til: '/h/mine' };

/** Skjemaets taster (DD-11 presisert 2026-09-26). Ingen enkelttast virker mens et felt har fokus. */
const TASTER: Tast[] = [
  ['Tab / Shift+Tab', 'neste og forrige felt, videre til neste ledd'],
  ['Enter', 'åpner leddet på en rad eller en brikke'],
  ['Ctrl+Enter', 'Neste · Send inn'],
  ['← →', 'flytter valget i retning, kilde og oppdatering'],
];

const FELTNAVN: Partial<Record<Felt, string>> = {
  title: 'Hva heter den?',
  change: 'Endringen',
  audience: 'For hvem',
  effect: 'Hva tror du skjer?',
  metricName: 'Måltallet',
  sourceText: 'Kilden',
  unit: 'Enheten',
  whatWeLose: 'Mister vi noe om vi venter?',
  background: 'Hvorfor tror du det?',
  differenceNote: 'Hva er annerledes med din?',
};

export function Innsending({ ruteId, ruteLedd }: { ruteId?: string; ruteLedd?: string }) {
  const naviger = useNavigate();
  const smal = useSmal();
  const me = useMe();
  const config = useHypoteseConfig();
  const oppfriskConfig = useOppfriskConfig();
  const hentPaaNytt = useHentHypotesePaaNytt();
  const sendInn = useSendInn();
  const oppfriskMeg = useOppfriskMeg();
  const kart = useKart();
  useEffect(() => {
    kart.meldInn('Skjemaet', TASTER);
    return () => kart.rydd();
  }, [kart]);

  const [lokaltLedd, setLokaltLedd] = useState<string>('tittel');
  const aapent = ruteId ? (ruteLedd ?? null) : lokaltLedd;
  const aapentRef = useRef(aapent);
  aapentRef.current = aapent;

  const lagring = useUtkastLagring({
    // Første lagring: adressen får utkastets id, og leddet som står åpent.
    onOpprettet: (id) => void naviger(`/h/utkast/${id}/${aapentRef.current ?? 'tittel'}`, { replace: true }),
  });

  // Et utkast åpnet fra en lenke (eller på den andre enheten): hent det og legg det i skjemaet.
  const hentet = useHypotese(ruteId && ruteId !== lagring.id ? ruteId : null);
  useEffect(() => {
    const h = hentet.data;
    if (!h || h.id === lagring.id) return;
    if (h.status !== 'utkast') {
      void naviger(h.number ? `/h/${h.number}` : MINE.til, { replace: true });
      return;
    }
    lagring.last(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hentet.data]);

  // «Ny hypotese» fra et åpent utkast: et tomt skjema. Utkastet som sto åpent, er lagret.
  useEffect(() => {
    if (!ruteId && lagring.id) {
      lagring.nullstill();
      setLokaltLedd('tittel');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruteId]);

  const min = config.data?.minFieldLength;
  const mangler = useMemo(() => (min === undefined ? [] : porter(lagring.skjema, min)), [lagring.skjema, min]);

  // /h/utkast/{id} uten ledd: første ledd som mangler noe, ellers Les over.
  useEffect(() => {
    if (!ruteId || ruteLedd || lagring.id !== ruteId || min === undefined) return;
    const foerste = LEDD.find((l) => !l.valgfri && !leddFylt(mangler, l.k));
    void naviger(`/h/utkast/${ruteId}/${foerste?.k ?? LES_OVER}`, { replace: true });
  }, [ruteId, ruteLedd, lagring.id, min, mangler, naviger]);

  const gaaTil = useCallback(
    (ledd: string) => {
      // Hvert ledd-bytte lagrer straks (ikke vent på pausen i skrivingen).
      void lagring.lagreNaa();
      if (lagring.id) void naviger(`/h/utkast/${lagring.id}/${ledd}`);
      else setLokaltLedd(ledd);
    },
    [lagring, naviger],
  );
  const gaaTilRef = useRef(gaaTil);
  gaaTilRef.current = gaaTil;

  const [navnAapent, setNavnAapent] = useState(false);
  const harNavn = Boolean(me.data?.displayName);

  // --- H3.3: kilden fra katalogen, spørsmålet til kildens eier og «Jeg vet ikke tallet ennå» -----
  // Utkastet slik tjeneren har det (cachen som autolagringen skriver): spørsmålet, kildestatusen.
  const lagret = useHypotese(lagring.id);
  const kilder = useKilder({}, Boolean(lagring.id));
  const kildeInfo: MetricSourceRef | undefined = lagring.skjema.metricSourceId
    ? (kilder.data?.items.find((k) => k.id === lagring.skjema.metricSourceId) ?? (lagret.data?.metricSource?.id === lagring.skjema.metricSourceId ? lagret.data.metricSource : undefined))
    : undefined;
  const sporsmaal = lagret.data?.sourceQuestion;
  const [panel, setPanel] = useState<Vei | null>(null);
  const [melding, setMelding] = useState<string | null>(null);
  const utloeser = useRef<HTMLButtonElement | null>(null);
  const velgForslag = useVelgForslag();
  const tenant = me.data?.tenantName ?? 'dere';
  const brukKilde = useCallback(
    (k: MetricSource) => {
      lagring.oppdater({ metricSourceId: k.id, sourceText: '', sourceClaim: undefined, updateFrequency: undefined });
      void lagring.lagreNaa();
      setMelding(fyltInn(k));
      // Under 900 lukker arket seg; statuslinjen står i skjemaet (M2).
      if (smal) setPanel(null);
    },
    [lagring, smal],
  );
  const skrivSelv = useCallback(() => {
    setPanel(null);
    if (aapentRef.current !== 'kilde') gaaTilRef.current('kilde');
    setTimeout(() => document.getElementById('f-kilde')?.focus(), 0);
  }, []);
  const velg = useCallback(
    async (bruk: boolean) => {
      if (!sporsmaal) return;
      const ok = await lagring.lagreNaa();
      const versjon = lagring.versjon();
      if (!ok || versjon === null) return;
      velgForslag.mutate({ id: sporsmaal.id, bruk, versjon }, { onSuccess: (h) => lagring.last(h) });
    },
    [sporsmaal, lagring, velgForslag],
  );
  // Leddet byttes: panelet lukkes ikke av seg selv (DD-47), men meldingen hører til det som ble gjort.
  useEffect(() => setMelding(null), [aapent]);

  // W1 først, ved første «Ny hypotese»: uten navn kan personen skrive utkast, men ikke sende inn.
  if ((!ruteId && me.data && !harNavn && !lagring.id) || navnAapent) {
    return (
      <NavnSide
        tittel="Ny hypotese"
        onLagret={() => setNavnAapent(false)}
        onLukk={() => (navnAapent ? setNavnAapent(false) : void naviger(MINE.til))}
      />
    );
  }

  if (ruteId && hentet.isError && lagring.id !== ruteId) {
    return (
      <Flate tittel="Utkast" tilbake={MINE}>
        {/* K2: et utkast som ikke finnes, sier det samme som et du ikke har tilgang til. */}
        <Varsel>{erNettfeil(hentet.error) ? 'Fikk ikke hentet utkastet. Sjekk nettet og prøv igjen.' : 'Fant ikke utkastet. Det kan være sendt inn, slettet, eller ikke ditt.'}</Varsel>
      </Flate>
    );
  }

  const laster = Boolean(ruteId) && lagring.id !== ruteId;

  if (aapent === LES_OVER) {
    return (
      <LesOver
        skjema={lagring.skjema}
        mangler={mangler}
        min={min}
        harNavn={harNavn}
        tilstand={lagring.tilstand}
        lagretKl={lagring.lagretKl}
        sender={sendInn.isPending}
        onAapne={gaaTil}
        onNavn={() => setNavnAapent(true)}
        onSendInn={async () => {
          const lagret = await lagring.lagreNaa();
          if (!lagret || !lagring.id) return;
          sendInn.mutate(lagring.id, {
            onSuccess: (h) => void naviger(`/h/${h.number}`, { state: { sendt: true } }),
            onError: (e) => {
              // Tjeneren fant noe klienten ikke så: minimumet kan være hevet, eller navnet borte.
              if (erApiFeil(e) && e.code === 'display_name_missing') void oppfriskMeg();
              if (erApiFeil(e) && e.code === 'hypothesis_incomplete') {
                void oppfriskConfig();
                if (lagring.id) void hentPaaNytt(lagring.id);
              }
            },
          });
        }}
        feil={sendInn.error}
        leietaker={me.data?.tenantName}
        navn={me.data?.displayName}
        tilbakeTil={lagring.id ? `/h/utkast/${lagring.id}/tittel` : MINE.til}
        kilde={kildeInfo}
        lagret={lagret.data}
        ligner={
          <LignerPaa
            id={lagring.id}
            runde={LES_OVER}
            aktiv={lagring.skjema.title.trim().length >= TITTEL_MIN}
            skjema={lagring.skjema}
            config={config.data}
            smal={smal}
            tittelTag="h2"
            onLagre={(patch) => {
              lagring.oppdater(patch);
              void lagring.lagreNaa();
            }}
          />
        }
      />
    );
  }

  const ledd = LEDD.find((l) => l.k === aapent) ?? LEDD[0]!;
  const tittel = lagring.id ? 'Utkast' : 'Ny hypotese';
  const status = <Lagret tilstand={lagring.tilstand} kl={lagring.lagretKl} kort={smal} />;
  const lange = forLange(lagring.skjema);
  const grunn = lagring.konflikt ? 'Velg versjon først.' : lange[0] ? `Kort ned «${FELTNAVN[lange[0].felt] ?? leddFor(lange[0].felt)}» med ${lange[0].over} tegn først.` : null;

  // «Neste»: neste ledd som mangler noe; etter det siste, Les over. De valgfrie hoppes over.
  const neste = () => {
    const her = LEDD.findIndex((l) => l.k === ledd.k);
    const kandidat = LEDD.slice(her + 1).find((l) => !l.valgfri && !leddFylt(mangler, l.k));
    gaaTil(kandidat?.k ?? LES_OVER);
  };

  const venstre = lagring.tilstand === 'nett' ? null : !lagring.id ? (
    <Tekstknapp onClick={() => void naviger(MINE.til)}>Lukk</Tekstknapp>
  ) : (
    <Tekstknapp
      onClick={async () => {
        await lagring.lagreNaa();
        void naviger(MINE.til);
      }}
      testid="h-fortsett-senere"
    >
      Fortsett senere
    </Tekstknapp>
  );

  const nett =
    lagring.tilstand === 'nett' ? (
      // Ingenting er tapt ennå: teksten står i minnet (K8: ingen localStorage). Lukkes fanen,
      // spør nettleseren først. «Fortsett senere» er skjult — den ville forlatt siden.
      <Varsel tone="info" rolle="status">
        Uten nett. Teksten står her så lenge fanen er åpen, og lagres når nettet er tilbake.
      </Varsel>
    ) : lagring.tilstand === 'feil' ? (
      <Varsel handling={<Tekstknapp onClick={() => void lagring.lagreNaa()}>Prøv igjen</Tekstknapp>}>{lagring.feiltekst ?? 'Utkastet ble ikke lagret.'} Teksten står her.</Varsel>
    ) : null;

  const konfliktKort = lagring.konflikt ? <Konfliktkort konflikt={lagring.konflikt} onBrukLagret={lagring.brukLagret} onBehold={lagring.beholdDenne} /> : null;
  const konfliktLedd = lagring.konflikt ? leddFor(lagring.konflikt.felt[0]!) : null;
  const deler = delsetning(lagring.skjema, { aapent: ledd.k, kildenavn: kildeInfo?.name });
  const fot = <Fot venstre={venstre} grunn={grunn} primar={{ label: 'Neste', onClick: neste, opptatt: laster, testid: 'h-neste' }} />;

  // H3.3: det som kommer rundt feltet — svaret fra kildens eier over måltallet (W8), «Jeg vet ikke
  // tallet ennå» under feltet i de tre leddene som handler om tallet (W5), og panelet.
  const vetIkkeLedd = ledd.k === 'maaltall' || ledd.k === 'kilde' || ledd.k === 'idag';
  const svarKort =
    ledd.k === 'maaltall' && sporsmaal?.status === 'besvart' && sporsmaal.suggestion ? <SvarKort q={sporsmaal} opptatt={velgForslag.isPending} feil={velgForslag.isError} onVelg={(bruk) => void velg(bruk)} /> : null;
  const vetIkkeKnapp = vetIkkeLedd ? (
    <button
      ref={utloeser}
      type="button"
      className={smal ? 'panel-knapp-sekundaer h-vet-ikke h-vet-ikke-full' : 'panel-knapp-sekundaer h-vet-ikke'}
      aria-expanded={panel !== null}
      aria-controls={panel !== null && !smal ? 'h-panel' : undefined}
      onClick={() => (panel ? setPanel(null) : setPanel(ledd.k === 'kilde' ? 'kilder' : 'veier'))}
      data-testid="h-vet-ikke"
    >
      <Ikon navn="help-circle-outline" />
      Jeg vet ikke tallet ennå
    </button>
  ) : null;
  const statuslinje =
    melding && (smal || panel === null) && ledd.k === 'kilde' ? (
      <p role="status" className="h-panel-melding">
        <Ikon navn="checkmark-outline" />
        {melding}
      </p>
    ) : null;
  const panelet =
    panel !== null && vetIkkeLedd ? (
      <VetIkkeTallet
        id={lagring.id}
        start={panel}
        smal={smal}
        tenant={tenant}
        megId={me.data?.id}
        deler={deler}
        kilde={kildeInfo}
        sporsmaal={sporsmaal}
        melding={melding}
        panelId="h-panel"
        utloeser={utloeser}
        onBrukKilde={brukKilde}
        onSkrivSelv={skrivSelv}
        onLukk={() => setPanel(null)}
      />
    ) : null;
  const lagreAnnen = (patch: Pick<Skjema, 'supersedesId' | 'differenceNote'>) => {
    lagring.oppdater(patch);
    void lagring.lagreNaa();
  };
  const lignerAktiv = lagring.skjema.title.trim().length >= TITTEL_MIN;

  if (smal) {
    return (
      <Flate tittel={tittel} tilbake={MINE} status={status} fot={fot} bredde="skjema">
        {nett}
        {laster ? (
          <div role="status" aria-busy="true" aria-label="Laster" className="panel-skjelett">
            <span className="panel-skjelett-linje panel-skjelett-tittel" />
            <span className="panel-skjelett-linje" />
          </div>
        ) : (
          <>
            <div className="h-setning-boks">
              <Brikker deler={deler} onAapne={gaaTil} />
            </div>
            <h2 className="h-sporsmal" id={`sp-${ledd.k}`}>
              {ledd.sp}
            </h2>
            {svarKort}
            <LeddFelt k={ledd.k} s={lagring.skjema} oppdater={lagring.oppdater} spId={`sp-${ledd.k}`} kilde={kildeInfo} tenant={tenant} />
            {statuslinje}
            {konfliktKort}
            {/* M2: knappen rett under feltet, i full bredde; kortene under den. */}
            {vetIkkeKnapp}
            <LignerPaa id={lagring.id} runde={ledd.k} aktiv={lignerAktiv} skjema={lagring.skjema} config={config.data} smal onLagre={lagreAnnen} tom={ledd.k !== 'effekt' && ledd.k !== 'idag' ? <p className="h-hjelp">{ledd.hjelp}</p> : null} />
          </>
        )}
        {panelet}
      </Flate>
    );
  }

  return (
    <Flate tittel={tittel} tilbake={MINE} status={status} fot={fot} bredde="skjema">
      {nett}
      <div className="h-skjema">
        <ol aria-label="Leddene" className="h-ledd">
          {laster
            ? [0, 1, 2, 3, 4].map((i) => (
                <li key={i} className="h-ledd-skjelett" aria-hidden="true">
                  <span className="panel-skjelett-linje" />
                </li>
              ))
            : LEDD.map((l) =>
                l.k === ledd.k ? (
                  <li key={l.k} aria-current="step" className="h-ledd-aapent" data-testid={`h-ledd-${l.k}`}>
                    <h2 className="h-sporsmal" id={`sp-${l.k}`}>
                      {l.sp}
                    </h2>
                    {svarKort}
                    <LeddFelt k={l.k} s={lagring.skjema} oppdater={lagring.oppdater} spId={`sp-${l.k}`} kilde={kildeInfo} tenant={tenant} />
                    {statuslinje}
                    {konfliktKort && konfliktLedd === l.k ? konfliktKort : null}
                    {vetIkkeKnapp}
                  </li>
                ) : (
                  <LukketLedd key={l.k} k={l.k} sp={l.sp} valgfri={l.valgfri} svar={svarFor(l.k, lagring.skjema, kildeInfo)} fylt={min !== undefined && leddFylt(mangler, l.k)} onAapne={() => gaaTil(l.k)} />
                ),
              )}
          {laster ? null : (
            <li>
              <button type="button" className="h-ledd-rad h-ledd-lesover" onClick={() => gaaTil(LES_OVER)} data-testid="h-til-les-over">
                <span aria-hidden="true" />
                Les over
              </button>
            </li>
          )}
        </ol>
        {panelet ?? (
          <div className="h-setning-kol">
            {konfliktKort && konfliktLedd !== ledd.k ? konfliktKort : null}
            <div className="h-setning-hode">
              {lagring.id ? (
                <span className="h-pille h-pille-utkast">
                  <Ikon navn="document-outline" />
                  Utkast
                </span>
              ) : null}
              <p className={lagring.skjema.title.trim() ? 'h-setning-tittel' : 'h-setning-tittel h-setning-tittel-tom'}>{lagring.skjema.title.trim() || 'Uten tittel'}</p>
            </div>
            <Brikker deler={deler} str="h3" onAapne={gaaTil} />
            {/* W2: kortene står der tipset sto; uten treff kommer tipset tilbake (W1). */}
            <LignerPaa id={lagring.id} runde={ledd.k} aktiv={lignerAktiv} skjema={lagring.skjema} config={config.data} smal={false} onLagre={lagreAnnen} tom={config.data?.helpText ? <p className="h-tips">{config.data.helpText}</p> : null} />
          </div>
        )}
      </div>
    </Flate>
  );
}

/** Én lukket rad: ✓ når leddet holder porten, stiplet sirkel når noe mangler, ingenting på de valgfrie. */
function LukketLedd({ k, sp, svar, fylt, valgfri, onAapne }: { k: LeddNoekkel; sp: string; svar: string | null; fylt: boolean; valgfri?: boolean; onAapne: () => void }) {
  return (
    <li>
      <button type="button" className="h-ledd-rad" onClick={onAapne} aria-label={`${sp} ${svar ?? (valgfri ? 'Valgfritt' : 'Ikke fylt ut')}`} data-testid={`h-ledd-${k}`}>
        {valgfri && !svar ? <span aria-hidden="true" /> : fylt ? <Ikon navn="checkmark-outline" className="h-ledd-fylt" /> : <span aria-hidden="true" className="h-ledd-tom" />}
        {svar ? (
          <span className="h-ledd-tekst">
            <span className="h-ledd-sp">{sp}</span>
            <span className="h-ledd-svar">{svar}</span>
          </span>
        ) : (
          <span className="h-ledd-tekst h-ledd-tekst-tom">
            <span className="h-ledd-sp">{sp}</span>
            <span className="h-ledd-ikke">{valgfri ? 'Valgfritt' : 'Ikke fylt ut'}</span>
          </span>
        )}
      </button>
    </li>
  );
}

/**
 * 409 `draft_changed_elsewhere` (W3b): begge navngitt, to like omriss-knapper — ingen er
 * riktigere. Ingenting skrives før personen har valgt. Kontrakten sier ikke HVOR den andre
 * versjonen ble lagret, så kortet sier «et annet sted», ikke «fra mobilen din». Endringsskjemaet
 * (W8b) bruker samme kort med sin egen overskrift — det er ikke et utkast.
 */
export function Konfliktkort({ konflikt, onBrukLagret, onBehold, hode = 'Utkastet er lagret et annet sted siden du åpnet det' }: { konflikt: Konflikt; onBrukLagret: () => void; onBehold: () => void; hode?: string }) {
  const felt = konflikt.felt[0]!;
  const der = String((konflikt.server as unknown as Record<string, unknown>)[felt] ?? '').trim();
  return (
    <div role="group" aria-labelledby="h-konflikt-h" className="h-konflikt" data-testid="h-konflikt">
      <div id="h-konflikt-h" className="h-konflikt-hode">
        <Ikon navn="cloud-offline-outline" className="h-konflikt-ikon" />
        {hode}
      </div>
      <p className="h-tekst-dempet">
        {der ? <>Der står «{der}» i «{FELTNAVN[felt] ?? LEDD.find((l) => l.k === leddFor(felt))!.sp}». </> : null}
        Velg hvilken som skal gjelde — ingenting overskrives før du velger.
      </p>
      <div className="h-konflikt-valg">
        <button type="button" className="panel-knapp-sekundaer" onClick={onBrukLagret} data-testid="h-bruk-lagret">
          Bruk den lagrede
        </button>
        <button type="button" className="panel-knapp-sekundaer" onClick={onBehold} data-testid="h-behold">
          Behold denne
        </button>
      </div>
    </div>
  );
}

/**
 * «Les over» (W4, W5; M3a): tittelen, hele setningen stor, og faktaene. Det som mangler står
 * TO steder med samme ord — som ⚠-brikke i setningen og som én setning ved den låste knappen —
 * FØR kallet. «Fyll ut» åpner leddet som mangler. «Send inn» er `POST /{id}/submit`; nummeret
 * deles ut der. Serverens `message` vises aldri.
 */
function LesOver({
  skjema,
  mangler,
  min,
  harNavn,
  tilstand,
  lagretKl,
  sender,
  onAapne,
  onNavn,
  onSendInn,
  feil,
  leietaker,
  navn,
  tilbakeTil,
  kilde,
  lagret,
  ligner,
}: {
  kilde: MetricSourceRef | undefined;
  lagret: Hypothesis | undefined;
  ligner: ReactNode;
  skjema: Skjema;
  mangler: Mangel[];
  min: number | undefined;
  harNavn: boolean;
  tilstand: ReturnType<typeof useUtkastLagring>['tilstand'];
  lagretKl: string | null;
  sender: boolean;
  onAapne: (ledd: string) => void;
  onNavn: () => void;
  onSendInn: () => void;
  feil: Error | null;
  leietaker: string | undefined;
  navn: string | undefined;
  tilbakeTil: string;
}) {
  const smal = useSmal();
  // Ukjent er strengest: før minimumet er hentet, er «Send inn» låst med grunnen (ux-flow-architect).
  const tjenerNekter = erApiFeil(feil) && feil.code === 'hypothesis_incomplete' && mangler.length === 0;
  const grunn =
    min === undefined
      ? 'Henter kravene til hypotesen …'
      : tilstand === 'nett'
        ? 'Utkastet er ikke lagret — venter på nett.'
        : tilstand === 'konflikt'
          ? 'Velg versjon i skjemaet først.'
          : tjenerNekter
            ? `${leietaker ?? 'Leietakeren'} har endret kravene. Se over leddene og prøv igjen.`
            : laasgrunn(mangler, { harNavn });
  const foerste = mangler[0];
  const venstre = !harNavn ? (
    <Tekstknapp onClick={onNavn} testid="h-sett-navn">
      Sett navnet
    </Tekstknapp>
  ) : foerste ? (
    <Tekstknapp onClick={() => onAapne(foerste.ledd)} testid="h-fyll-ut">
      Fyll ut
    </Tekstknapp>
  ) : (
    <Tekstknapp onClick={() => onAapne('tittel')}>Tilbake</Tekstknapp>
  );
  const annenFeil = feil && !(erApiFeil(feil) && ['hypothesis_incomplete', 'display_name_missing'].includes(feil.code)) ? (erNettfeil(feil) ? 'Fikk ikke sendt: uten nett. Teksten står, prøv igjen når nettet er tilbake.' : erApiFeil(feil) ? feil.brukertekst : 'Fikk ikke sendt. Prøv igjen.') : null;

  const deler = delsetning(skjema, { visMangler: true, kildenavn: kilde?.name });
  const iDag = skjema.baselineState === 'ikke_maalt' ? 'Ikke målt ennå' : skjema.baselineValue.trim() ? `I dag ${skjema.baselineValue.trim()}${skjema.unit.trim() ? ` ${skjema.unit.trim()}` : ''}` : null;
  // W4: kilden fra kildelisten, og «Definisjonen er avklart · bekreftet av …» når kildens eier eller
  // katalogen har sagt det — det «Send inn» setter (`sourceStatus` i utkast viser det på forhånd).
  const avklartAv = skjema.metricSourceId && lagret?.sourceStatus === 'verifisert' && lagret.sourceVerifiedBy ? lagret : undefined;

  const hoved = (
    <>
      <h2 className="h-lesover-tittel">{skjema.title.trim() || 'Uten tittel'}</h2>
      <Brikker deler={deler} str={smal ? 'h3' : 'h1'} onAapne={onAapne} />
      <p className="h-hjelp">{smal ? 'Trykk' : 'Klikk'} på et ledd for å endre det.</p>
      {annenFeil ? <Varsel>{annenFeil}</Varsel> : null}
      {/* Skjemaets fakta ved siden av setningen (W4): i dag, kilden og navnet det står med. */}
      <dl className="h-fakta">
        {iDag ? <Faktarad navn={iDag} verdi={skjema.baselineOn ? `${skjema.baselineState === 'maalt' ? 'målt' : 'per'} ${kortDato(skjema.baselineOn)}` : ''} /> : null}
        {skjema.metricSourceId ? (
          <Faktarad navn={kilde ? `Oppdateres ${frekvensTekst(kilde.updateFrequency).toLowerCase()}` : 'Kilden'} verdi={`${kilde?.name ?? 'Kilden'} · fra kildelisten`} />
        ) : skjema.updateFrequency || skjema.sourceText.trim() ? (
          <Faktarad navn={skjema.updateFrequency ? `Oppdateres ${frekvensTekst(skjema.updateFrequency).toLowerCase()}` : 'Kilden'} verdi={skjema.sourceText.trim()} />
        ) : null}
        {avklartAv ? <Faktarad navn="Definisjonen er avklart" verdi={`bekreftet av ${navnAv(avklartAv.sourceVerifiedBy)}${avklartAv.sourceVerifiedAt ? ` ${kortDatoAv(avklartAv.sourceVerifiedAt)}` : ''}`} /> : null}
        {skjema.sourceClaim && !skjema.metricSourceId ? <Faktarad navn="Finnes tallet der?" verdi={paastandTekst(skjema.sourceClaim)} /> : null}
        {navn ? <Faktarad navn="Med navnet mitt" verdi={navn} /> : null}
      </dl>
    </>
  );

  return (
    <Flate
      tittel="Les over"
      tilbake={{ navn: 'Skjemaet', til: tilbakeTil }}
      status={<Lagret tilstand={tilstand} kl={lagretKl} kort={smal} />}
      fot={<Fot venstre={venstre} grunn={grunn} primar={{ label: sender ? 'Sender …' : 'Send inn', onClick: onSendInn, opptatt: sender, ikon: 'send-outline', testid: 'h-send-inn' }} />}
      bredde={smal ? 'les' : 'skjema'}
    >
      {smal ? (
        <>
          {hoved}
          {ligner}
        </>
      ) : (
        // W4: kortene til høyre for setningen, så de er synlige når «Send inn» trykkes.
        <div className="h-lesover-kol">
          <div className="h-lesover-hoved">{hoved}</div>
          <div className="h-lesover-hoyre">{ligner}</div>
        </div>
      )}
    </Flate>
  );
}

/**
 * W8: svaret fra kildens eier som ett kort i leddet for måltallet, over feltet — navn, dato,
 * forslaget, én setning om hva det betyr, og én fylt knapp. Uansett valg lukkes spørsmålet, og
 * kildens eier ser ikke utkastet lenger (Kh15).
 */
function SvarKort({ q, opptatt, feil, onVelg }: { q: SourceQuestion; opptatt: boolean; feil: boolean; onVelg: (bruk: boolean) => void }) {
  const SIER = { verifisert: 'definisjonen er avklart', uavklart_definisjon: 'definisjonen er uklar', maa_bygges: 'kilden må bygges' } as const;
  const vurdering = q.sourceAssessment ? `${stor(fornavn(q.answerer))} sier ${SIER[q.sourceAssessment]}.` : '';
  return (
    <section className="hb-kort h-svar-kort" aria-labelledby="h-svar-h" data-testid="h-svar-kort">
      <div className="hb-kort-hode">
        <b id="h-svar-h">
          <Ikon navn="person-outline" /> {navnAv(q.answerer)} foreslår et måltall
        </b>
        {q.answeredAt ? <span className="hb-liten">{kortDatoAv(q.answeredAt)}</span> : null}
      </div>
      <p className="hb-tekst">{q.suggestion}</p>
      <p className="hb-dempet">{`${vurdering.trim()} Velger du forslaget, erstatter det teksten din.`.trim()}</p>
      {feil ? <Varsel>Fikk ikke lagret valget. Prøv igjen.</Varsel> : null}
      <div className="h-svar-knapper">
        <button type="button" className="panel-knapp" aria-busy={opptatt || undefined} onClick={opptatt ? undefined : () => onVelg(true)}>
          Bruk forslaget
        </button>
        <Tekstknapp onClick={() => onVelg(false)}>Behold mitt</Tekstknapp>
      </div>
    </section>
  );
}

function Faktarad({ navn, verdi }: { navn: string; verdi: string }) {
  return (
    <div className="h-faktarad">
      <dt>{navn}</dt>
      <dd>{verdi}</dd>
    </div>
  );
}
